/**
 * Gated HITL bridge for `propose_load_series`.
 *
 * Same gate as the other proposals (≤1-pending preamble, recoverable
 * `failed` outcomes the model re-asks against, Apply/Reject card): parse the
 * model's input → re-read the patient's studies from the PACS and check the
 * study and series exist → a {@link LoadSeriesProposal} the card renders.
 * Nothing happens until the clinician Applies. Apply hands the selection to
 * the existing DICOM import dialog through `requestImport` and opens it; the
 * dialog's own code selects the series and the clinician presses Load there.
 * Pure/React-free.
 */
import type { AgentDicomImportRequest } from '~/state/atoms/chatAtoms';
import type { LoadSeriesOutcome } from '../../contract';
import {
    asRecord,
    failedPlan,
    mutatingPreamble,
    type MutatingPlan,
    type MutatingToolCall,
    type PendingFlag,
} from '../core';
import { resolveTarget, type PatientDataDeps } from './patientQueries';

/** Same cap as the server contract (MAX_SERIES_PER_LOAD). */
export const MAX_SERIES_PER_LOAD = 10;

export interface LoadSeriesDeps extends PatientDataDeps, PendingFlag {
    /** Hand an accepted selection to the DICOM import dialog. */
    requestImport(request: AgentDicomImportRequest): void;
    /** Open the DICOM import dialog. */
    openImportDialog(): void;
}

export interface LoadSeriesProposal {
    readonly toolCallId: string;
    readonly patientId: string;
    readonly patientName: string;
    readonly targetName: string;
    readonly studyDescription: string;
    readonly studyDate: string;
    readonly series: ReadonlyArray<{
        readonly description: string;
        readonly modality: string;
        readonly sliceCount: number;
    }>;
    apply(): Promise<LoadSeriesOutcome>;
    reject(): LoadSeriesOutcome;
    /** No viewport overlay for a load; kept for the shared channel. */
    showPreview(): void;
    hidePreview(): void;
}

export type LoadSeriesPlan = MutatingPlan<LoadSeriesProposal>;

interface LoadInput {
    readonly patientId: string;
    readonly studyInstanceUid: string;
    readonly seriesInstanceUids: ReadonlyArray<string>;
    readonly targetName?: string;
}

function readInput(input: unknown): LoadInput | undefined {
    const o = asRecord(input);
    if (
        !o ||
        typeof o.patientId !== 'string' ||
        !o.patientId ||
        typeof o.studyInstanceUid !== 'string' ||
        !o.studyInstanceUid ||
        !Array.isArray(o.seriesInstanceUids) ||
        o.seriesInstanceUids.length === 0 ||
        o.seriesInstanceUids.length > MAX_SERIES_PER_LOAD ||
        !o.seriesInstanceUids.every((u) => typeof u === 'string' && !!u) ||
        (o.targetName !== undefined && typeof o.targetName !== 'string')
    ) {
        return undefined;
    }
    return {
        patientId: o.patientId,
        studyInstanceUid: o.studyInstanceUid,
        // De-duplicate so the dialog toggles each series once.
        seriesInstanceUids: [...new Set(o.seriesInstanceUids as string[])],
        ...(o.targetName ? { targetName: o.targetName } : {}),
    };
}

export async function planProposeLoadSeries(
    call: MutatingToolCall,
    deps: LoadSeriesDeps
): Promise<LoadSeriesPlan> {
    const pre = mutatingPreamble(call, deps);
    if (pre !== 'ok') {
        return pre;
    }
    if (!deps.connectorMode) {
        return failedPlan(
            call.toolCallId,
            'loading from a PACS needs the viewer in connector mode'
        );
    }
    const parsed = readInput(call.input);
    if (!parsed) {
        return failedPlan(call.toolCallId, 'invalid propose_load_series input');
    }

    let targetName: string | undefined;
    let patient;
    try {
        targetName = await resolveTarget(parsed.targetName, deps);
        if (!targetName) {
            return failedPlan(
                call.toolCallId,
                'the connector has no inbound PACS target configured'
            );
        }
        patient = await deps.getPatientStudies(parsed.patientId, targetName);
    } catch {
        return failedPlan(
            call.toolCallId,
            'could not read the patient from the PACS; try again'
        );
    }
    if (!patient) {
        return failedPlan(
            call.toolCallId,
            `patient "${parsed.patientId}" was not found in ${targetName}; ` +
                'call list_patient_studies and use the IDs it returned'
        );
    }
    const study = patient.studies?.find(
        (s) => s.studyInstanceUID === parsed.studyInstanceUid
    );
    if (!study) {
        return failedPlan(
            call.toolCallId,
            'that study is not in the patient record; call ' +
                'list_patient_studies and use a studyInstanceUid it returned'
        );
    }
    const chosen = parsed.seriesInstanceUids.map((uid) =>
        study.series?.find((s) => s.seriesInstanceUID === uid)
    );
    if (chosen.some((s) => !s)) {
        return failedPlan(
            call.toolCallId,
            'one or more series are not in that study; call ' +
                'list_patient_studies and use seriesInstanceUids it returned'
        );
    }
    const series = chosen.filter(
        (s): s is NonNullable<typeof s> => s !== undefined
    );
    if (series.some((s) => !s.slices?.length)) {
        return failedPlan(
            call.toolCallId,
            'one or more series have no images to load'
        );
    }

    const patientName = patient.patientName ?? '';
    const request: AgentDicomImportRequest = {
        patientId: parsed.patientId,
        patientName,
        targetName,
        studyInstanceUid: parsed.studyInstanceUid,
        seriesInstanceUids: parsed.seriesInstanceUids,
    };
    const proposal: LoadSeriesProposal = {
        toolCallId: call.toolCallId,
        patientId: parsed.patientId,
        patientName,
        targetName,
        studyDescription: study.studyDescription ?? '',
        studyDate: study.studyDate ?? '',
        series: series.map((s) => ({
            description: s.seriesDescription ?? '',
            modality: s.modality ?? '',
            sliceCount: s.slices?.length ?? 0,
        })),
        apply: async () => {
            deps.requestImport(request);
            deps.openImportDialog();
            return {
                status: 'applied',
                seriesCount: request.seriesInstanceUids.length,
            };
        },
        reject: () => ({ status: 'rejected' }),
        showPreview: () => undefined,
        hidePreview: () => undefined,
    };
    return { kind: 'proposal', toolCallId: call.toolCallId, proposal };
}
